import React, { useEffect, useState } from "react";
import { Drawer, Box, Grid, TextField, Typography, IconButton, Button as MuiButton, CircularProgress, MenuItem, FormControlLabel, Switch } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { validationUsuarioUpdateSchema } from "../../../schemas/validationSchema.ts";
import { useFeedback } from "../../../contexts/FeedbackContext.tsx";
import usuarioService from "../../../services/usuarioService.ts";
import { formatBrazilPhone } from "../../../utils/phone.util.ts";
import PermissoesPreview from "../../PermissoesPreview/index.tsx";
import { usePermissoes } from "../../../contexts/PermissoesContext.tsx";
import { TipoUsuario } from "../../../types/Permissoes.ts";

interface UpdateUsuarioDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  onUpdate?: (r: any) => void;
  usuario: any;
}

// Mesma receita de cores dos demais módulos — sem ThemeProvider do MUI,
// estados de label/borda/desabilitado caem no tema claro padrão do MUI, sem
// contraste no fundo escuro. CSS vars do design system em vez de tokens de tema.
const fieldSx = {
  "& .MuiInputLabel-root": { color: "var(--ink-faint)" },
  "& .MuiInputLabel-root.Mui-focused": { color: "var(--blue)" },
  "& .MuiInputLabel-root.Mui-disabled": { color: "var(--ink-faint)" },
  "& .MuiOutlinedInput-root": {
    color: "var(--ink)",
    "& fieldset": { borderColor: "var(--border-strong)" },
    "&:hover fieldset": { borderColor: "var(--border-strong)" },
    "&.Mui-focused fieldset": { borderColor: "var(--blue)" },
    "&.Mui-disabled fieldset": { borderColor: "var(--border)" },
  },
  "& .MuiInputBase-input.Mui-disabled": {
    WebkitTextFillColor: "var(--ink-faint)",
    color: "var(--ink-faint)",
  },
  "& .MuiFormHelperText-root": { color: "var(--red)" },
};

const scrollSx = {
  "&::-webkit-scrollbar": { width: 8 },
  "&::-webkit-scrollbar-track": { background: "transparent" },
  "&::-webkit-scrollbar-thumb": { background: "var(--border-strong)", borderRadius: 4 },
};

const UpdateUsuarioDrawer: React.FC<UpdateUsuarioDrawerProps> = ({ isOpen, onClose, onUpdate, usuario }) => {
  const { showFeedback, } = useFeedback();
  const { catalogo, usuario: usuarioLogado } = usePermissoes();
  const [isLoading, setIsLoading] = useState(false);

  const [tipo, setTipo] = useState<TipoUsuario>("OPERADOR");
  const [podeExcluirAgendamento, setPodeExcluirAgendamento] = useState(false);
  const [podeVerLog, setPodeVerLog] = useState(false);

  // Guardados para o painel poder comparar e avisar o que o usuario perde.
  const [tipoOriginal, setTipoOriginal] = useState<TipoUsuario | null>(null);
  const [chavesOriginais, setChavesOriginais] = useState({
    podeExcluirAgendamento: false,
    podeVerLog: false,
  });

  // Um ADM mexendo na propria funcao poderia deixar a empresa sem nenhum
  // administrador. O backend recusa; aqui so evitamos oferecer o caminho.
  const editandoPropriaConta = !!usuarioLogado && usuarioLogado.id === usuario?.id;

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
    reset,
  } = useForm({
    resolver: yupResolver(validationUsuarioUpdateSchema),
  });

  useEffect(() => {
    if (usuario) {
      reset({
        nome: usuario.nome || "",
        telefone: formatBrazilPhone(usuario.telefone) || "",
      });
      const t: TipoUsuario = usuario.tipo || "OPERADOR";
      setTipo(t);
      setTipoOriginal(t);
      setPodeExcluirAgendamento(!!usuario.podeExcluirAgendamento);
      setPodeVerLog(!!usuario.podeVerLog);
      setChavesOriginais({
        podeExcluirAgendamento: !!usuario.podeExcluirAgendamento,
        podeVerLog: !!usuario.podeVerLog,
      });
    }
  }, [usuario, reset]);

  const onSubmit = async (data: any) => {
    const payload: {
      id: string;
      telefone: string;
      nome?: string;
      tipo?: TipoUsuario;
      podeExcluirAgendamento?: boolean;
      podeVerLog?: boolean;
    } = {
      id: usuario.id,
      telefone: data.telefone,
    };

    if (data.nome !== undefined) payload.nome = data.nome;

    // Funcao e permissoes so viajam quando de fato mudaram: mandar os campos
    // sempre faria o backend recusar a edicao da propria conta mesmo quando o
    // ADM so quis corrigir o telefone.
    if (!editandoPropriaConta && tipo !== tipoOriginal) payload.tipo = tipo;
    if (podeExcluirAgendamento !== chavesOriginais.podeExcluirAgendamento) {
      payload.podeExcluirAgendamento = podeExcluirAgendamento;
    }
    if (podeVerLog !== chavesOriginais.podeVerLog) payload.podeVerLog = podeVerLog;

    try {
      setIsLoading(true);
      const resp = await usuarioService.update(payload);
      showFeedback(resp.message, "success");
      onUpdate?.(resp);
      onClose();
    } catch (err: any) {
      showFeedback(err.message || "Erro ao alterar usuário", "error");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Drawer
      anchor="right"
      open={isOpen}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 460 },
          bgcolor: "var(--surface)",
          color: "var(--ink)",
          borderLeft: "1px solid var(--border)",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      {/* Cabeçalho fixo */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", px: 3, py: 2.5, borderBottom: "1px solid var(--border)", flex: "0 0 auto" }}>
        <Typography variant="h6" sx={{ fontWeight: 800 }}>Alterar Usuário</Typography>
        <IconButton onClick={onClose} size="small" sx={{ color: "var(--ink-faint)" }} aria-label="Fechar">
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Corpo com scroll — flex:1 + overflowY:auto, o resto do Drawer fica fixo */}
      <Box
        component="form"
        id="update-usuario-form"
        autoComplete="off"
        onSubmit={handleSubmit(onSubmit)}
        sx={{ flex: 1, overflowY: "auto", px: 3, py: 3, ...scrollSx }}
      >
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField label="E-mail" size="small" fullWidth disabled sx={fieldSx} value={usuario?.email || ""} />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Nome completo" size="small" fullWidth sx={fieldSx}
              {...register("nome")} autoComplete="off"
              error={!!errors.nome} helperText={errors.nome?.message as string}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Telefone*" size="small" fullWidth sx={fieldSx}
              value={watch("telefone") || ""}
              onChange={(e) => setValue("telefone", formatBrazilPhone(e.target.value), { shouldValidate: true })}
              error={!!errors.telefone} helperText={errors.telefone?.message as string}
            />
          </Grid>

          {/*
            O campo "Nova senha" foi removido daqui. Ele mandava a senha num
            endpoint que gravava sem nenhuma checagem de posse - o id vinha no
            corpo, entao dava para trocar a senha de qualquer usuario, de
            qualquer empresa. Agora o backend nem aceita mais o campo, e manter
            o input seria pior que remover: o admin digitaria uma senha nova,
            veria "sucesso" e nada teria mudado.
          */}
          <Grid item xs={12}>
            <TextField
              select
              label="Função*"
              size="small"
              fullWidth
              sx={fieldSx}
              value={tipo}
              disabled={editandoPropriaConta}
              helperText={
                editandoPropriaConta
                  ? "Você não pode alterar a própria função. Peça a outro Administrador."
                  : undefined
              }
              onChange={(e) => setTipo(e.target.value as TipoUsuario)}
              SelectProps={{
                MenuProps: {
                  sx: {
                    zIndex: 1400,
                    "& .MuiPaper-root": { bgcolor: "var(--surface)", color: "var(--ink)" },
                  },
                },
              }}
            >
              {(catalogo?.funcoes ?? []).map((f) => (
                <MenuItem key={f.codigo} value={f.codigo}>{f.rotulo}</MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid item xs={12}>
            <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
              Permissões individuais
            </Typography>
            <Typography variant="caption" sx={{ color: "var(--ink-faint)" }}>
              Independentes da função — use com critério.
            </Typography>
            <FormControlLabel
              sx={{ display: "flex", mt: 1, ml: 0, color: "var(--ink)" }}
              control={
                <Switch
                  size="small"
                  checked={podeExcluirAgendamento}
                  onChange={(e) => setPodeExcluirAgendamento(e.target.checked)}
                />
              }
              label={
                <span style={{ fontSize: 13 }}>
                  Excluir agendamento{" "}
                  <span style={{ color: "var(--ink-faint)" }}>(apaga do banco, sem volta)</span>
                </span>
              }
            />
            <FormControlLabel
              sx={{ display: "flex", ml: 0, color: "var(--ink)" }}
              control={
                <Switch
                  size="small"
                  checked={podeVerLog}
                  onChange={(e) => setPodeVerLog(e.target.checked)}
                />
              }
              label={<span style={{ fontSize: 13 }}>Verificar log de atividades</span>}
            />
          </Grid>

          <Grid item xs={12}>
            <PermissoesPreview
              tipo={tipo}
              podeExcluirAgendamento={podeExcluirAgendamento}
              podeVerLog={podeVerLog}
              tipoAnterior={tipoOriginal}
              chavesAnteriores={chavesOriginais}
            />
          </Grid>
        </Grid>
      </Box>

      {/* Rodapé fixo */}
      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1.5, px: 3, py: 2.5, borderTop: "1px solid var(--border)", flex: "0 0 auto" }}>
        <MuiButton onClick={onClose} disabled={isLoading} sx={{ color: "var(--ink-soft)" }}>
          Cancelar
        </MuiButton>
        <MuiButton type="submit" form="update-usuario-form" variant="contained" disabled={isLoading} disableElevation>
          {isLoading ? <CircularProgress size={18} sx={{ color: "#fff" }} /> : "Salvar Alterações"}
        </MuiButton>
      </Box>
    </Drawer>
  );
};

export default UpdateUsuarioDrawer;
